import './style.css';
import { PLACES, PLOT_D, PLOT_W, generateBricks, generateGrass } from './cloud';
import { F, buildStructures } from './diagram';
import type { Prim } from './diagram';
import { finish } from './finish';
import { closePage, isOpen, openPage } from './pages';
import { closeSection, isSectionOpen, openSection } from './section';
import type { SectionDef } from './section';
import { greenhouseSection } from './section-greenhouse';
import { wellSection } from './section-well';
import { PROJECTS } from './projects';
import { iconPath } from './icons';
import { closeWork, isWorkOpen, openWork } from './work';
import { initIntro } from './intro';
import { clamp01, css, drawSky, lightAt, mix, setMuting, WARM } from './sky';
import { liveClock, paletteHour, setLocation } from './time';
import type { Light, RGB } from './sky';

// An isometric masterplan. The landscape is flat ground with a hatched-grass texture over the whole window,
// a few thousand wind-blown grass points and vector bricks; the structures are section drawings on top.

const canvas = document.getElementById('world') as HTMLCanvasElement;
const ctx = canvas.getContext('2d', { alpha: false }) as CanvasRenderingContext2D;
const debugEl = document.getElementById('debug') as HTMLElement;
const intro = initIntro(); // phones open on a field of grass that parts once the plot is drawn and the phone is turned sideways
const params = new URLSearchParams(location.search);
const debug = params.has('debug');
debugEl.hidden = !debug;

const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
const coarse = matchMedia('(pointer: coarse)').matches;
const lite = coarse || window.innerWidth < 700;

const grass = generateGrass(lite);
const bricks = generateBricks();
const structures = buildStructures();
const { x: GX, y: GY, z: GZ, hf: HF, ph: PH, start: GS } = grass;
const PX = new Float32Array(grass.n);
const PY = new Float32Array(grass.n);
const hotIdx = new Int32Array(grass.n);
const HC = Array.from({ length: 6 }, (_, k) => Math.cos(Math.PI / 6 + (Math.PI / 3) * k));
const HS = Array.from({ length: 6 }, (_, k) => Math.sin(Math.PI / 6 + (Math.PI / 3) * k));

// ---- layout ----------------------------------------------------------------
let w = 0, h = 0, dpr = 1, U0 = 5;
/** On phones the caption is docked at the bottom, so the map lives in the free band above it (px from the top). */
let stageT = 0, stageB = 0, stageC = 0, stageH = 0;
/** A phone held sideways: the caption is a narrow card at the top left, the chapter bar is slim and the map fills the area to the right of the card. */
const landscapePhone = (): boolean => coarse && w > h && h < 520;
const phone = (): boolean => w < 700 && !landscapePhone();
/** Either phone layout: markers carry their names beside the dot and only the neighbours of the current place are named. */
const compact = (): boolean => phone() || landscapePhone();
const LP_CAP = 292; // width the caption card takes at the left in landscape, with its margin
let viewCX = 0, availW = 0;
function resize(): void {
  w = window.innerWidth;
  h = window.innerHeight;
  const cap = coarse || w < 700 ? 1.5 : 2;
  dpr = Math.min(window.devicePixelRatio || 1, cap);
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const lp = landscapePhone();
  document.documentElement.classList.toggle('lp', lp);
  viewCX = lp ? LP_CAP + (w - LP_CAP) / 2 : w / 2;
  availW = lp ? w - LP_CAP : w;
  if (lp) { stageT = 0; stageB = h - 46; } else if (phone()) { stageT = 64; stageB = Math.max(stageT + 200, h - 315); } else { stageT = 0; stageB = h - 84; }
  stageC = lp ? stageB / 2 : phone() ? (stageT + stageB) / 2 : h * 0.52;
  stageH = lp ? stageB : phone() ? stageB - stageT : h * 0.69;
  // fit the plot: about 147 units wide and 101 tall in isometric
  U0 = lp ? Math.min((availW * 0.96) / 147.2, (stageH * 0.94) / 101) : phone() ? Math.min((w * 1.3) / 147.2, (stageH * 0.94) / 101) : Math.min((w * 0.9) / 147.2, (h * 0.72) / 101);
}
resize();
addEventListener('resize', resize);
// some phones report the old size right after a turn; asking again a moment later settles it
addEventListener('orientationchange', () => setTimeout(() => dispatchEvent(new Event('resize')), 300));

// ---- chapters (read from the text version in index.html) --------------------
interface Chapter { id: string; label: string; role: string; tag: string; text: string }
const chapters: Chapter[] = Array.from(document.querySelectorAll<HTMLElement>('#text-version [data-chapter]')).map((el) => ({
  id: el.dataset.chapter ?? '',
  role: el.dataset.role ?? '',
  tag: el.dataset.tag ?? '',
  label: el.querySelector('h2')?.textContent ?? '',
  text: el.querySelector('p')?.textContent ?? '',
}));
const shortName = (c: Chapter): string => c.label.replace(/^The /, '');
/** The design chapters (Architecture, Planning, Interviews) are districts: their structures are the content, so they only travel. */
const districtIds = new Set<string>(PROJECTS.map((p) => p.chapter));
const isDistrict = (i: number): boolean => districtIds.has(chapters[i]?.id ?? '');
const projChapter = (id: string): number => chapters.findIndex((c) => c.id === PROJECTS.find((p) => p.id === id)?.chapter);

// camera: the ground point at the centre of the screen, and zoom relative to the fit
const cam = { x: 50, y: 35, z: 1 };
let chapter = 0;
/** A place is framed together with the one before and the one after it, so both stay in view and their markers stay clickable. */
const camFor = (i: number): { x: number; y: number; z: number } => {
  if (i === 0) return { x: 50, y: 35, z: 1 };
  const isWork = isDistrict(i); // a district is framed as one, all its projects at once
  const ids = isWork ? PROJECTS.filter((p) => p.chapter === chapters[i].id).map((p) => p.id) : [i - 1, i, i + 1].filter((k) => k >= 1 && k < chapters.length).map((k) => chapters[k].id);
  const ps = ids.map((id) => PLACES.find((q) => q.id === id)).filter((p): p is (typeof PLACES)[number] => !!p);
  let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9; // in isometric screen units
  for (const p of ps) {
    const sx = (p.x - p.y) * 0.866, sy = (p.x + p.y) * 0.5;
    const mx = isWork ? (compact() ? 17 : 12) : 9; // a ring's names stick out sideways, so they get room too
    x0 = Math.min(x0, sx - mx); x1 = Math.max(x1, sx + mx);
    y0 = Math.min(y0, sy - p.top * 0.9 - (compact() ? 9 : 7) - (isWork ? 5 : 0)); y1 = Math.max(y1, sy + (isWork ? 9 : 6)); // phones leave room above for the marker labels
  }
  const z = Math.max(1.2, Math.min(compact() ? 3.2 : isWork ? 3.6 : 2.7, (availW * 0.8) / ((x1 - x0) * U0), ((compact() ? stageH * 0.8 : h * 0.5)) / ((y1 - y0) * U0)));
  const cx = (x0 + x1) / 2 / 0.866, cy = ((y0 + y1) / 2) * 2; // x - y, x + y of the ground point under the middle of the frame
  return { x: (cy + cx) / 2, y: (cy - cx) / 2, z };
};
let camTarget = camFor(0);
/** How far each design chapter's hub has spread into its projects (0 = one structure, 1 = the whole row). */
const spread: Record<string, number> = { architecture: 0, planning: 0, interviews: 0 };
const spreadOf = (id: string): number => spread[PROJECTS.find((p) => p.id === id)?.chapter ?? ''] ?? 1;
addEventListener('resize', () => { camTarget = camFor(chapter); placeHint(); });

const navList = document.querySelector('#chapters ol') as HTMLOListElement;
const caption = document.getElementById('caption') as HTMLElement;
const capEyebrow = caption.querySelector('.eyebrow') as HTMLElement;
const capTitle = caption.querySelector('h2') as HTMLElement;
const capBody = caption.querySelector('.body') as HTMLElement;
const hint = document.getElementById('hint') as HTMLElement;
hint.textContent = coarse ? 'Drag to scan the site. Swipe up to travel.' : 'Move to scan the site. Scroll to travel.';

const markInteracted = (): void => { hint.classList.add('gone'); };
/** On phones the hint sits in the gap just above the docked caption, clear of the map. */
function placeHint(): void {
  if (w >= 700 || landscapePhone()) { hint.style.bottom = ''; hint.style.top = ''; return; }
  hint.style.top = 'auto';
  hint.style.bottom = `${Math.round(h - caption.getBoundingClientRect().top + 10)}px`;
}

const capOpen = caption.querySelector('.open') as HTMLButtonElement;
const capWalk = caption.querySelector('.walk') as HTMLButtonElement;
/** Places with an in-depth section view (a building cut open, rooms side by side). The rest open their page. */
const SECTIONS: Record<string, SectionDef> = { well: wellSection, greenhouse: greenhouseSection };
const overlayOpen = (): boolean => isOpen() || isSectionOpen() || isWorkOpen();
const capPrev = caption.querySelector('.prev') as HTMLButtonElement;
const capNext = caption.querySelector('.next') as HTMLButtonElement;
const buttons: HTMLButtonElement[] = []; // indexed by chapter; the overview (0) has no tab
const pins: HTMLButtonElement[] = [];
const pinText: number[] = []; // characters in each marker's name, for placing it beside its dot
const pinLayer = document.createElement('div');
pinLayer.className = 'pins';
document.body.appendChild(pinLayer);

chapters.forEach((ch, i) => {
  if (i === 0) { // the overview is the home state: a small menu button that zooms back out to the start
    const li = document.createElement('li');
    li.className = 'home';
    const b = document.createElement('button');
    b.type = 'button';
    b.setAttribute('aria-label', 'Overview: zoom out to the whole plot');
    b.title = 'Overview';
    b.innerHTML = '<svg viewBox="0 0 20 14" width="18" height="12" aria-hidden="true"><path d="M1 1.5h18M1 7h18M1 12.5h18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none"/></svg>';
    b.addEventListener('click', () => go(0));
    li.appendChild(b);
    navList.appendChild(li);
    buttons[0] = b;
    return;
  }
  const li = document.createElement('li');
  const b = document.createElement('button');
  b.type = 'button';
  b.innerHTML = `<span>${shortName(ch)}</span><small>${ch.tag}</small>`;
  b.setAttribute('aria-label', `${ch.label}: ${ch.role}`);
  b.addEventListener('click', () => go(i, !isDistrict(i))); // a district is opened one project at a time, from the map
  li.appendChild(b);
  navList.appendChild(li);
  buttons[i] = b;

  const pin = document.createElement('button');
  pin.type = 'button';
  pin.className = 'pin';
  pin.setAttribute('aria-label', ch.label);
  pin.innerHTML = `<span class="dot"></span><span class="name">${shortName(ch)}</span>`;
  pin.addEventListener('click', () => go(i, !isDistrict(i)));
  pinLayer.appendChild(pin);
  pins[i] = pin;
  pinText[i] = shortName(ch).length + 2;
});

const projTitle = (id: string): string => document.getElementById(`p-${id}`)?.textContent?.trim() ?? id;
/** The name on the map: short, so the labels fit side by side. */
const projShort = (id: string): string => document.getElementById(`p-${id}`)?.dataset.short ?? projTitle(id);
const projText = (id: string): string => document.getElementById(`p-${id}`)?.nextElementSibling?.textContent?.trim() ?? '';
const projPins = new Map<string, HTMLButtonElement>();
PROJECTS.forEach((pr) => {
  const pin = document.createElement('button');
  pin.type = 'button';
  pin.className = 'pin proj';
  pin.setAttribute('aria-label', projTitle(pr.id));
  pin.innerHTML = `<span class="dot"></span><span class="name">${projShort(pr.id)}</span>`;
  pin.addEventListener('click', () => openProject(pr.id));
  pinLayer.appendChild(pin);
  projPins.set(pr.id, pin);
});

let openTimer = 0;
/** Travel to a place; with `open`, its page follows once the camera has arrived. */
function go(i: number, open = false, mode: 'walk' | 'page' = 'walk'): void {
  const k = Math.max(0, Math.min(chapters.length - 1, i));
  markInteracted();
  if (k !== chapter || currentShown !== k) {
    chapter = k;
    camTarget = camFor(k);
    showChapter(k);
  }
  clearTimeout(openTimer);
  if (open && k > 0) openTimer = window.setTimeout(() => openChapter(k, mode), reduceMotion ? 0 : 650);
}

/** Open one project's sheets; the camera first moves in on its district. */
function openProject(id: string): void {
  const k = PROJECTS.findIndex((p) => p.id === id);
  const ci = projChapter(id);
  if (k < 0 || ci < 0) return;
  const pr = PROJECTS[k];
  if (chapter !== ci) go(ci);
  history.replaceState(null, '', `#${id}`);
  const step = (to: number) => (to >= 0 && to < PROJECTS.length ? { label: projTitle(PROJECTS[to].id), fn: () => openProject(PROJECTS[to].id) } : undefined);
  openWork({
    title: projTitle(id),
    text: projText(id),
    sheets: pr.sheets,
    onClose: () => history.replaceState(null, '', location.pathname + location.search),
    prev: step(k - 1),
    next: step(k + 1),
  });
}

function openChapter(k: number, mode: 'walk' | 'page' = 'walk'): void {
  const id = chapters[k].id;
  const section = document.querySelector<HTMLElement>(`#text-version [data-chapter="${id}"]`);
  if (!section) return;
  history.replaceState(null, '', `#${id}`);
  const clearHash = (): void => history.replaceState(null, '', location.pathname + location.search);
  const def = SECTIONS[id];
  if (def && mode === 'walk') {
    const link = (to: number) => (to > 0 && to < chapters.length ? { label: shortName(chapters[to]), fn: () => go(to, true) } : undefined);
    openSection(def, {
      source: section,
      day: () => light.day,
      ground: () => groundCol,
      onRead: () => openChapter(k, 'page'),
      onClose: clearHash,
      before: link(k - 1),
      after: link(k + 1),
    });
    return;
  }
  const step = (to: number) => ({ label: shortName(chapters[to]), fn: () => { closePage(); go(to, true); } });
  openPage(section, buttons[k] ?? null, clearHash, {
    prev: k > 1 ? step(k - 1) : undefined,
    next: k < chapters.length - 1 ? step(k + 1) : undefined,
  });
}

document.getElementById('home')?.addEventListener('click', () => { closePage(); closeSection(); closeWork(); go(0); });
capOpen.addEventListener('click', () => go(chapter, true, 'page'));
capWalk.addEventListener('click', () => go(chapter, true, 'walk'));
capPrev.addEventListener('click', () => go(chapter - 1));
capNext.addEventListener('click', () => go(chapter + 1));

let currentShown = -1;
function showChapter(i: number): void {
  if (i === currentShown) return;
  currentShown = i;
  const ch = chapters[i];
  capEyebrow.textContent = ch.role;
  capTitle.textContent = ch.label;
  capBody.textContent = ch.text;
  capOpen.hidden = i === 0;
  capWalk.hidden = !SECTIONS[ch.id];
  capPrev.hidden = i === 0;
  capPrev.textContent = `\u2039 ${i === 1 ? 'Overview' : i > 1 ? shortName(chapters[i - 1]) : ''}`;
  capNext.hidden = i >= chapters.length - 1;
  capNext.textContent = `${i < chapters.length - 1 ? shortName(chapters[i + 1]) : ''} \u203a`;
  buttons.forEach((b, k) => (k === i ? b.setAttribute('aria-current', 'true') : b.removeAttribute('aria-current')));
  const cur = buttons[i]; // on narrow screens the chapter bar scrolls: keep the current tab in view
  if (cur && navList.scrollWidth > navList.clientWidth) {
    const br = cur.getBoundingClientRect(), nr = navList.getBoundingClientRect();
    navList.scrollTo({ left: navList.scrollLeft + (br.left - nr.left) - (nr.width - br.width) / 2, behavior: reduceMotion ? 'auto' : 'smooth' });
  }
  pins.forEach((p, k) => { p.classList.toggle('on', k === i); p.classList.toggle('prev', k === i - 1); p.classList.toggle('next', k === i + 1); p.classList.toggle('all', i === 0); });
  caption.classList.remove('swap');
  void caption.offsetWidth;
  if (!reduceMotion) caption.classList.add('swap');
}

// ---- input -----------------------------------------------------------------
const pointer = { x: -9999, y: -9999, active: false, down: false, startY: 0, stepped: false };

addEventListener('pointermove', (e) => {
  pointer.x = e.clientX;
  pointer.y = e.clientY;
  if (e.pointerType === 'touch') {
    if (pointer.down) {
      pointer.active = true;
      const dy = e.clientY - pointer.startY;
      if (!pointer.stepped && Math.abs(dy) > 70) { pointer.stepped = true; go(chapter + (dy < 0 ? 1 : -1)); }
    }
  } else pointer.active = true;
});
addEventListener('pointerdown', (e) => {
  pointer.down = true;
  pointer.stepped = false;
  pointer.x = e.clientX;
  pointer.y = pointer.startY = e.clientY;
  if (e.pointerType === 'touch') pointer.active = true;
});
const release = (e: PointerEvent): void => {
  pointer.down = false;
  if (e.pointerType === 'touch') pointer.active = false;
};
addEventListener('pointerup', release);
addEventListener('pointercancel', release);
document.addEventListener('mouseleave', () => { pointer.active = false; });

let wheelAcc = 0;
let wheelAt = 0;
addEventListener('wheel', (e) => {
  if (overlayOpen()) return; // the page or the section view takes the wheel
  e.preventDefault();
  wheelAcc += (e.deltaY + e.deltaX) * (e.deltaMode === 1 ? 32 : 1);
  const now = performance.now();
  if (Math.abs(wheelAcc) > 60 && now - wheelAt > 500) {
    go(chapter + (wheelAcc > 0 ? 1 : -1));
    wheelAt = now;
    wheelAcc = 0;
  } else if (now - wheelAt > 500 && Math.abs(wheelAcc) < 1) wheelAcc = 0;
}, { passive: false });

addEventListener('keydown', (e) => {
  if (overlayOpen() || (e.target instanceof HTMLInputElement)) return;
  if (e.key === 'Enter' && chapter > 0 && !(e.target instanceof HTMLButtonElement)) { openChapter(chapter); e.preventDefault(); return; }
  switch (e.key) {
    case 'ArrowRight': case 'ArrowDown': case 'PageDown': case ' ': go(chapter + 1); break;
    case 'ArrowLeft': case 'ArrowUp': case 'PageUp': go(chapter - 1); break;
    case 'Home': go(0); break;
    case 'End': go(chapters.length - 1); break;
    default: return;
  }
  e.preventDefault();
});

// ---- tapping a structure on the plan ----
/** The project whose structure is under a point, if any (screen pixels). */
/** A project's icon on screen: a badge standing a little above its spot on the ground. */
function iconAt(p: { x: number; y: number }): { x: number; y: number; r: number } {
  const r = Math.max(13, Math.min(27, view.Ue * 1.9));
  return { x: view.ox + (p.x - p.y) * view.A, y: view.oy + (p.x + p.y) * view.Bc - r - 9, r };
}
function projectAt(px: number, py: number): string | null {
  let best: string | null = null, bd = 1e9;
  for (const pr of PROJECTS) {
    const p = PLACES.find((q) => q.id === pr.id);
    if (!p || spreadOf(pr.id) < 0.85) continue; // until the chapter has opened there is only its hub
    const g = iconAt(p);
    const d = Math.hypot(px - g.x, py - g.y);
    if (d < g.r + 8 && d < bd) { bd = d; best = pr.id; }
  }
  return best;
}
/** A closed chapter's hub under a point: tapping it opens the chapter. */
function hubAt(px: number, py: number): number {
  for (const id of Object.keys(spread)) {
    if (spread[id] > 0.5) continue;
    const p = PLACES.find((q) => q.id === id)!;
    const cx = view.ox + (p.x - p.y) * view.A, cy0 = view.oy + (p.x + p.y) * view.Bc, cy1 = cy0 - p.top * view.C;
    if (Math.abs(px - cx) < view.Ue * 3.6 && py > cy1 - view.Ue * 0.6 && py < cy0 + view.Ue * 1.8) return chapters.findIndex((c) => c.id === id);
  }
  return -1;
}
canvas.addEventListener('click', (e) => {
  if (overlayOpen()) return;
  const hi = hubAt(e.clientX, e.clientY);
  if (hi > 0) { go(hi); return; }
  const id = projectAt(e.clientX, e.clientY);
  if (!id) return;
  const ci = projChapter(id);
  if (chapter !== ci) go(ci); else openProject(id);
});
canvas.addEventListener('pointermove', (e) => { canvas.style.cursor = !overlayOpen() && e.pointerType === 'mouse' && (projectAt(e.clientX, e.clientY) || hubAt(e.clientX, e.clientY) > 0) ? 'pointer' : ''; });

// ---- light and colour ------------------------------------------------------
const GROUND: RGB = [84, 184, 71];
const GRASS: RGB = [249, 239, 137];
const PINK_A: RGB = [233, 165, 160];
const PINK_B: RGB = [226, 156, 154];
const HOT_TINT: RGB = [255, 236, 230];
const TIER_ALPHA = [0.95, 0.68, 0.42];

let topShade = 1;
/** A flat, upward-facing surface under the current sun (or moon). */
function lit(c: RGB, L: Light): RGB {
  const day = mix([c[0] * topShade, c[1] * topShade, c[2] * topShade], WARM, L.warm * 0.08);
  const k = 0.78 + 0.22 * topShade; // moonlit, not black: the ground and grass stay readable at night
  const night: RGB = [(c[0] * 0.5 + 24) * k, (c[1] * 0.52 + 36) * k, (c[2] * 0.58 + 62) * k];
  return mix(night, day, L.day);
}

const rgb = (c: RGB, a = 1): string => `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${a})`;
const fillCache = new Map<number, string>();
let ink = '#14161e';
let paper: RGB = [255, 255, 255];
let diagram: Record<number, { c: RGB; a: number }> = {};
function fillFor(f: number, s: number): string {
  const key = f * 1000 + Math.round(s * 100);
  let v = fillCache.get(key);
  if (v === undefined) {
    const d = diagram[f];
    v = rgb([d.c[0] * s, d.c[1] * s, d.c[2] * s], d.a);
    fillCache.set(key, v);
  }
  return v;
}

let groundCol = '', hatchKey = '';
let hatch: CanvasPattern | null = null;
const tierCols: string[] = [];
const tierHot: string[] = [];
let brickA = '', brickB = '', brickHot = '';

function makeHatch(col: string): CanvasPattern | null {
  const size = 384;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d');
  if (!g) return null;
  let a = 5;
  const r = (): number => { a = (a * 1664525 + 1013904223) >>> 0; return a / 4294967296; };
  g.strokeStyle = col;
  g.lineWidth = 1.2;
  g.lineCap = 'butt';
  for (const [alpha, n] of [[0.9, 520], [0.6, 400], [0.32, 300]] as [number, number][]) {
    g.globalAlpha = alpha;
    g.beginPath();
    for (let i = 0; i < n; i++) {
      const x = r() * size, y = r() * size, len = 5 + r() * 6, lean = (r() - 0.5) * 2.4;
      for (const dx of [0, size, -size]) for (const dy of [0, size, -size]) { // wrap, so the tile repeats without a seam
        if (x + dx < -4 || x + dx > size + 4 || y + dy < -14 || y + dy > size + 14) continue;
        g.moveTo(x + dx, y + dy);
        g.lineTo(x + dx + lean, y + dy - len);
      }
    }
    g.stroke();
  }
  return ctx.createPattern(c, 'repeat');
}

/** Rebuilt when the light changes: ground, grass, brick and drawing colours. */
function buildColours(L: Light): void {
  const t = clamp01((L.hour - 6) / 12);
  const el = 0.45 + 0.5 * Math.sin(Math.PI * t);
  topShade = Math.min(1.15, 0.48 + 0.62 * (0.15 + 0.85 * L.day) * Math.sin(el));
  setMuting(true);
  groundCol = css(lit(GROUND, L));
  const grassCol = lit(GRASS, L);
  const key = css(grassCol);
  if (key !== hatchKey) { hatchKey = key; hatch = makeHatch(key); }
  for (let i = 0; i < 3; i++) {
    tierCols[i] = css(grassCol, TIER_ALPHA[i]);
    tierHot[i] = css(mix(grassCol, HOT_TINT, 0.5), 1);
  }
  // the path keeps its Vinoy pink at every hour: no desaturation, and only a faint dusk shade
  brickA = css(mix(lit(PINK_A, L), PINK_A, 0.85), 1, 1);
  brickB = css(mix(lit(PINK_B, L), PINK_B, 0.85), 1, 1);
  brickHot = css(mix(mix(lit(PINK_A, L), PINK_A, 0.85), HOT_TINT, 0.5), 1, 1);
  // the drawing: dark ink on white paper at every hour
  const d = 1; // structures stay white with dark ink at every hour, so they do not invert at night
  ink = rgb(mix([206, 214, 232], [20, 22, 30], d));
  paper = mix([255, 255, 255], lit(GROUND, L), 0.2); // soft white taken a little toward the ground's green, so the structures sit in the landscape
  diagram = {
    [F.PAPER]: { c: paper, a: 0.9 }, // a touch of the ground shows through
    [F.POCHE]: { c: mix([206, 214, 232], [20, 22, 30], d), a: 1 },
    [F.GLASS]: { c: mix([110, 120, 140], [206, 212, 218], d), a: 0.42 },
    [F.FLOOR]: { c: mix(paper, [214, 208, 194], 0.6), a: 1 },
    // the map's structures are monotone: paper and ink only, so the old accent fills become one soft grey
    [F.LEAF]: { c: mix(paper, mix([206, 214, 232], [20, 22, 30], d), 0.35), a: 0.7 },
    [F.CORAL]: { c: mix(paper, mix([206, 214, 232], [20, 22, 30], d), 0.22), a: 1 },
    [F.LIME]: { c: mix(paper, mix([206, 214, 232], [20, 22, 30], d), 0.22), a: 1 },
    [F.COBALT]: { c: mix(paper, mix([206, 214, 232], [20, 22, 30], d), 0.22), a: 1 },
    [F.YELLOW]: { c: mix(paper, mix([206, 214, 232], [20, 22, 30], d), 0.22), a: 1 },
  };
  fillCache.clear();
}

// ---- frame loop ------------------------------------------------------------
const qLock = params.get('q');
let quality = qLock ? Number(qLock) : 1;
let ema = 1 / 60;
let slowFrames = 0, fastFrames = 0;
let last = performance.now();
/** Local clock hours chosen with the scrubber or `?hour=`; null follows the real clock. */
let manual: number | null = params.get('hour') !== null && params.get('hour') !== '' ? Number(params.get('hour')) : null;
const currentLight = (): Light => lightAt(paletteHour(manual ?? liveClock()));
let light = currentLight();
buildColours(light);
let lightClock = 0;
let fps = 60;
let cost = 0;

const INK = 'rgba(248, 243, 230, ';
const FT = 3.28084; // one grid unit is one metre of the original plan, shown in feet
const LW = [0, 0.7, 1.15, 1.9];

let frameNo = 0;
let view = { ox: 0, oy: 0, A: 1, Bc: 1, C: 1, Ue: 1 };
function frame(now: number): void {
  // behind an open page the map only needs an occasional redraw
  if (isSectionOpen()) { requestAnimationFrame(frame); return; } // the section view covers the map
  if ((isOpen() || isWorkOpen()) && !debug && frameNo++ % 6) { requestAnimationFrame(frame); return; }
  const dt = Math.min(0.05, Math.max(0.001, (now - last) / 1000));
  last = now;
  const t = now / 1000;
  const t0 = performance.now();

  lightClock += dt;
  if (lightClock > 0.5) { light = currentLight(); buildColours(light); lightClock = 0; }

  // camera glides, leaving each place at the same pace it arrives
  const k = 1 - Math.exp(-dt * (reduceMotion ? 14 : 3.4));
  for (const id of Object.keys(spread)) { // the open chapter's hub spreads; the others close up again
    const to = chapters[chapter]?.id === id ? 1 : 0;
    spread[id] = reduceMotion ? to : spread[id] + (to - spread[id]) * (1 - Math.exp(-dt * 2.6));
    if (Math.abs(to - spread[id]) < 0.002) spread[id] = to;
  }
  cam.x += (camTarget.x - cam.x) * k;
  cam.y += (camTarget.y - cam.y) * k;
  cam.z *= Math.pow(camTarget.z / cam.z, k);

  const Ue = U0 * cam.z;
  const A = 0.866 * Ue, Bc = 0.5 * Ue, C = 0.9 * Ue;
  const ox = viewCX - (cam.x - cam.y) * A;
  const oy = stageC - (cam.x + cam.y) * Bc;
  const projX = (gx: number, gy: number): number => ox + (gx - gy) * A;
  const projY = (gx: number, gy: number, gz: number): number => oy + (gx + gy) * Bc - gz * C;
  view = { ox, oy, A, Bc, C, Ue };

  // ground and hatched grass fill the whole window; a wash of the sky's colour sits over it
  ctx.fillStyle = groundCol;
  ctx.fillRect(0, 0, w, h);
  setMuting(false);
  ctx.globalAlpha = 0.16 + 0.14 * light.warm + 0.12 * (1 - light.day);
  drawSky(ctx, w, h, light, t, 0, 1);
  ctx.globalAlpha = 1;
  setMuting(true);
  if (hatch) {
    const sc = Math.pow(cam.z, 0.6) * (compact() ? 0.7 : 1);
    ctx.save();
    ctx.translate(ox, oy);
    ctx.scale(sc, sc);
    ctx.fillStyle = hatch;
    ctx.fillRect(-ox / sc, -oy / sc, w / sc, h / sc);
    ctx.restore();
  }

  // plot boundary
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 8]);
  ctx.strokeStyle = INK + '0.3)';
  ctx.beginPath();
  ([[0, 0], [PLOT_W, 0], [PLOT_W, PLOT_D], [0, PLOT_D]] as [number, number][]).forEach(([gx, gy], i) => {
    const px = projX(gx, gy), py = projY(gx, gy, 0);
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  });
  ctx.closePath();
  ctx.stroke();
  ctx.setLineDash([]);

  const R = (coarse ? 92 : 76) * Math.min(1.6, 0.7 + 0.3 * cam.z);
  const R2 = R * R;
  const scanning = pointer.active;
  const sweepX = reduceMotion ? -999 : ((t * 8) % 140) - 20;

  // bricks: two fills, plus the ones the pointer or the sweep line is over
  {
    const m = 40;
    const hot: number[] = [];
    const add = (i: number): void => {
      const gx = bricks.x[i], gy = bricks.y[i], r = bricks.r[i];
      for (let v = 0; v < 6; v++) {
        const vx = gx + r * HC[v], vy = gy + r * HS[v];
        const px = ox + (vx - vy) * A, py = oy + (vx + vy) * Bc;
        if (v === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
    };
    for (let pass = 0; pass < 2; pass++) {
      ctx.fillStyle = pass ? brickB : brickA;
      ctx.beginPath();
      for (let i = 0; i < bricks.n; i++) {
        if (bricks.col[i] !== pass) continue;
        const cx = ox + (bricks.x[i] - bricks.y[i]) * A, cy = oy + (bricks.x[i] + bricks.y[i]) * Bc;
        if (cx < -m || cx > w + m || cy < -m || cy > h + m) continue;
        let isHot = Math.abs(bricks.x[i] - sweepX) < 0.9;
        if (!isHot && scanning) { const dx = cx - pointer.x, dy = cy - pointer.y; isHot = dx * dx + dy * dy < R2 * 0.3; }
        if (isHot) { hot.push(i); continue; }
        add(i);
      }
      ctx.fill();
    }
    if (hot.length) {
      ctx.fillStyle = brickHot;
      ctx.beginPath();
      for (const i of hot) add(i);
      ctx.fill();
    }
  }

  // grass points: sway in the wind, bend away from the pointer, brighten under it
  const windAmp = reduceMotion ? 0.2 : 1;
  for (let i = 0; i < grass.n; i++) {
    let gx = GX[i], gy = GY[i];
    const hf = HF[i];
    const wv = (Math.sin(t * 1.4 + PH[i]) * 0.5 + Math.sin(t * 0.5 + gx * 0.09) * 0.8 + 0.2) * windAmp * hf * hf * 0.45;
    gx += wv; gy += wv * 0.3;
    let sx = ox + (gx - gy) * A, sy = oy + (gx + gy) * Bc - GZ[i] * C;
    if (scanning) {
      const dx = sx - pointer.x, dy = sy - pointer.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < R2) {
        const d = Math.sqrt(d2) + 0.001;
        const f = 1 - d / R;
        const push = f * f * R * 0.6 * hf;
        sx += (dx / d) * push;
        sy += (dy / d) * push * 0.5 + f * hf * C * 1.1;
      }
    }
    PX[i] = sx; PY[i] = sy;
  }
  const base = Math.min(7, Math.max(1.15, 0.3 * U0 * Math.pow(cam.z, 0.85)));
  const ww = base * 0.7, hh = base * 1.7;
  for (let tier = 0; tier < 3; tier++) {
    const s0 = GS[tier], cnt = Math.ceil((GS[tier + 1] - s0) * quality);
    let nh = 0;
    ctx.fillStyle = tierCols[tier];
    ctx.beginPath();
    for (let i = s0; i < s0 + cnt; i++) {
      const x = PX[i], y = PY[i];
      if (x < -8 || x > w + 8 || y < -8 || y > h + 8) continue;
      let hot = Math.abs(GX[i] - sweepX) < 0.9;
      if (!hot && scanning) { const dx = x - pointer.x, dy = y - pointer.y; hot = dx * dx + dy * dy < R2 * 0.3; }
      if (hot) hotIdx[nh++] = i;
      else ctx.rect(x - ww / 2, y - hh / 2, ww, hh);
    }
    ctx.fill();
    if (nh) {
      ctx.fillStyle = tierHot[tier];
      ctx.beginPath();
      for (let j = 0; j < nh; j++) { const i = hotIdx[j]; ctx.rect(PX[i] - ww * 0.65, PY[i] - hh * 0.65, ww * 1.3, hh * 1.3); }
      ctx.fill();
    }
  }

  // structures and trees, far to near, as section drawings
  {
    const lwz = Math.pow(cam.z, 0.75); // lines thicken as you zoom in, like a detail drawing
    ctx.strokeStyle = ink;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    // a chapter's hub shrinks and fades as the chapter opens
    const dyn = structures.map((st) => {
      if (st.hub) { const e = spread[st.hub], ee = e * e * (3 - 2 * e); return { st, e, s: 1 - 0.35 * ee, ox: 0, oy: 0, key: st.key, a: 1 - Math.min(1, ee * 2.2) }; }
      return { st, e: 1, s: 1, ox: 0, oy: 0, key: st.key, a: 1 };
    });
    dyn.sort((a, b) => a.key - b.key);
    for (const { st, s, ox, oy, a } of dyn) {
      if (a <= 0.01) continue;
      ctx.globalAlpha = a;
      const cx = st.cx ?? 0, cy = st.cy ?? 0;
      const tx = (v: number): number => (st.cx === undefined ? v : cx + (v - cx) * s + ox);
      const ty = (v: number): number => (st.cy === undefined ? v : cy + (v - cy) * s + oy);
      for (let q = 0; q < st.prims.length; q++) {
        const pr: Prim = st.prims[q];
        const p = pr.p;
        ctx.beginPath();
        if (pr.k === 2) {
          ctx.arc(projX(tx(p[0]), ty(p[1])), projY(tx(p[0]), ty(p[1]), p[2] * s), pr.r * Ue * 0.95 * s, 0, Math.PI * 2);
        } else {
          for (let i = 0; i < p.length; i += 3) {
            const px = projX(tx(p[i]), ty(p[i + 1])), py = projY(tx(p[i]), ty(p[i + 1]), p[i + 2] * s);
            if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
          }
          if (pr.k === 0) ctx.closePath();
        }
        if (pr.f) { ctx.fillStyle = fillFor(pr.f, pr.s); ctx.fill(); }
        if (pr.w && !(pr.k === 0 && pr.f)) { // filled faces carry no outline; bare lines (frames, poles, rails) stay as faint, transparent strokes
          ctx.globalAlpha = a * 0.4;
          ctx.lineWidth = LW[pr.w] * lwz; ctx.stroke();
          ctx.globalAlpha = a;
        }
      }
    }
    ctx.globalAlpha = 1;
  }

  // the projects: simple line icons on round paper badges, growing out of their chapter's hub as it opens
  for (const pr of PROJECTS) {
    const e = spreadOf(pr.id);
    if (e <= 0.01) continue;
    const p = PLACES.find((q) => q.id === pr.id)!, hub = PLACES.find((q) => q.id === pr.chapter)!;
    const ee = e * e * (3 - 2 * e);
    const gx = hub.x + (p.x - hub.x) * ee, gy = hub.y + (p.y - hub.y) * ee;
    const g = iconAt({ x: gx, y: gy }), r = g.r * (0.35 + 0.65 * ee);
    ctx.globalAlpha = Math.min(1, ee * 2.5);
    const bx = projX(gx, gy), by = projY(gx, gy, 0);
    ctx.fillStyle = 'rgba(10, 20, 14, 0.22)';
    ctx.beginPath(); ctx.ellipse(bx, by, r * 0.7, r * 0.28, 0, 0, Math.PI * 2); ctx.fill(); // its shadow on the ground
    ctx.strokeStyle = ink; ctx.lineWidth = 1.3;
    ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(bx, by - 9 * (0.35 + 0.65 * ee)); ctx.stroke(); // a short stem
    const cy = by - 9 * (0.35 + 0.65 * ee) - r;
    ctx.fillStyle = fillFor(F.PAPER, 1);
    ctx.beginPath(); ctx.arc(bx, cy, r, 0, Math.PI * 2); ctx.fill();
    ctx.save();
    ctx.translate(bx - r * 0.62, cy - r * 0.62);
    ctx.scale((r * 1.24) / 24, (r * 1.24) / 24);
    ctx.lineWidth = 1.6 / ((r * 1.24) / 24);
    ctx.stroke(iconPath(pr.id));
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  // ---- masterplan overlay ----
  ctx.fillStyle = INK + '0.5)';
  ctx.strokeStyle = INK + '0.4)';
  ctx.lineWidth = 1;
  ctx.font = '500 10px ui-monospace, Menlo, Consolas, monospace';
  ctx.beginPath();
  for (let ft = 0; ft <= 330; ft += 50) { // survey ticks every 50 feet along the two back edges
    const u = ft / FT;
    if (u <= PLOT_D) { const px = projX(0, u), py = projY(0, u, 0); ctx.moveTo(px, py); ctx.lineTo(px - 5, py - 3); }
    if (u <= PLOT_W) { const px = projX(u, 0), py = projY(u, 0, 0); ctx.moveTo(px, py); ctx.lineTo(px + 5, py - 3); }
  }
  ctx.stroke();
  if (cam.z < 1.6) for (let ft = 0; ft <= 300; ft += 100) ctx.fillText(ft === 300 ? '300 ft' : String(ft), projX(ft / FT, 0) + 8, projY(ft / FT, 0, 0) - 5);

  if (!reduceMotion && sweepX > 0 && sweepX < PLOT_W) {
    ctx.strokeStyle = 'rgba(255, 226, 220, 0.45)';
    ctx.beginPath();
    ctx.moveTo(projX(sweepX, 0), projY(sweepX, 0, 0));
    ctx.lineTo(projX(sweepX, PLOT_D), projY(sweepX, PLOT_D, 0));
    ctx.stroke();
  }

  // leader lines and pins
  const lift = compact() ? 26 : 58;
  for (const p of PLACES) {
    const pp = projPins.get(p.id);
    if (pp) { // a project: its name sits just outside its icon, away from the hub, so names fan out round the ring
      const hub = PLACES.find((q) => q.id === PROJECTS.find((q2) => q2.id === p.id)!.chapter)!;
      const g = iconAt(p);
      const hx = projX(hub.x, hub.y), hy = projY(hub.x, hub.y, 0) - g.r - 9;
      let dx = g.x - hx, dy = g.y - hy;
      const dl = Math.hypot(dx, dy) || 1; dx /= dl; dy /= dl;
      const hw = ((pp.querySelector('.name')?.textContent?.length ?? 8) * 3.6 + 8); // half the name's width, roughly
      const px = g.x + dx * (g.r + 6 + hw * Math.abs(dx)), py = g.y + dy * (g.r + 12 + 8 * Math.abs(dy));
      const on = chapter === projChapter(p.id) && spreadOf(p.id) > 0.7;
      pp.style.transform = `translate(${px.toFixed(1)}px, ${py.toFixed(1)}px) translate(-50%, -50%)`;
      const vis = on && px > -20 && px < w + 20 && py > -20 && py < (compact() ? stageB + 40 : h - 84);
      pp.style.opacity = vis ? '1' : '0';
      pp.style.pointerEvents = vis ? 'auto' : 'none';
      pp.tabIndex = vis ? 0 : -1;
      continue;
    }
    const ci = chapters.findIndex((c) => c.id === p.id);
    const el = pins[ci];
    if (!el) continue; // the gate has no marker
    const ax = projX(p.x, p.y), ay = projY(p.x, p.y, p.top);
    const py = compact() ? Math.max(ay - lift, stageT + 30) : ay - lift;
    const gone = isDistrict(chapter) && (compact() || ci !== chapter - 1); // among the projects only the place before keeps its marker
    if (!gone) { // no call-out line for a marker that is hidden
    ctx.strokeStyle = INK + (ci === chapter ? '0.95)' : '0.65)');
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(ax, py);
    ctx.stroke();
    ctx.fillStyle = INK + '0.95)';
    ctx.fillRect(ax - 1.5, ay - 1.5, 3, 3);
    }
    el.style.transform = `translate(${ax.toFixed(1)}px, ${py.toFixed(1)}px) translate(-50%, -100%)`;
    if (compact()) { // the name sits beside the dot, towards the open side of the screen, so it never lands on a structure
      const need = 18 + (pinText[ci] ?? 8) * 6.6 + 12; // the name's width, roughly, with its arrow
      const left = ax < viewCX ? ax - need >= 6 : ax + need > w - 6;
      el.classList.toggle('flip', left);
    }
    el.style.opacity = gone || ax < -20 || ax > w + 20 || (compact() ? ay > stageB + 4 : py < -20 || py > h - 84) ? '0' : '1';
    el.style.pointerEvents = gone ? 'none' : 'auto';
  }

  // north arrow and scale bar
  {
    const nx = w - 44, ny = compact() ? stageB - 30 : h - 132, len = 24;
    const vx = 0.866, vy = -0.5; // north is the -gy direction
    ctx.strokeStyle = INK + '0.85)';
    ctx.fillStyle = INK + '0.9)';
    ctx.beginPath();
    ctx.arc(nx, ny, 19, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(nx - vx * len * 0.5, ny - vy * len * 0.5);
    ctx.lineTo(nx + vx * len * 0.5, ny + vy * len * 0.5);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(nx + vx * len * 0.72, ny + vy * len * 0.72);
    ctx.lineTo(nx + vx * len * 0.3 - vy * 5, ny + vy * len * 0.3 + vx * 5);
    ctx.lineTo(nx + vx * len * 0.3 + vy * 5, ny + vy * len * 0.3 - vx * 5);
    ctx.fill();
    ctx.font = '600 10px ui-monospace, Menlo, Consolas, monospace';
    ctx.fillText('N', nx + vx * 31 - 4, ny + vy * 31 + 4);

    if (!compact() || cam.z < 1.4) { // on phones the scale bar is only drawn on the overview, where it has room
    let feet = 500;
    for (const f of [10, 25, 50, 100, 250, 500]) if ((f / FT) * A <= 150) feet = f;
    const units = feet / FT;
    const sx0 = 24, sy0 = (compact() ? stageB - 12 : h - 92) - units * Bc;
    ctx.beginPath();
    ctx.moveTo(sx0, sy0);
    ctx.lineTo(sx0 + units * A, sy0 + units * Bc);
    for (let i = 0; i <= 2; i++) {
      const tx = sx0 + (units * A * i) / 2, ty = sy0 + (units * Bc * i) / 2;
      ctx.moveTo(tx, ty);
      ctx.lineTo(tx - 4, ty - 7);
    }
    ctx.stroke();
    ctx.fillText(`0 \u2014 ${feet} ft`, sx0 + 2, sy0 - 12);
    }
  }

  finish(ctx, w, h, 0.3);

  cost += (performance.now() - t0 - cost) * 0.05;

  // adapt quality to the device
  ema += (dt - ema) * 0.05;
  if (!qLock) {
    if (ema > 0.026) { slowFrames++; fastFrames = 0; } else if (ema < 0.0185) { fastFrames++; slowFrames = 0; } else { slowFrames = 0; fastFrames = 0; }
    if (slowFrames > 20 && quality > 0.3) { quality = Math.max(0.3, quality * 0.85); slowFrames = 0; }
    if (fastFrames > 240 && quality < 1) { quality = Math.min(1, quality * 1.08); fastFrames = 0; }
  }

  if (debug) {
    fps += (1 / dt - fps) * 0.1;
    debugEl.textContent = `fps ${fps.toFixed(0)}  ms ${(ema * 1000).toFixed(1)}  js ${cost.toFixed(1)}\ngrass ${grass.n}  bricks ${bricks.n}  quality ${quality.toFixed(2)}\ndpr ${dpr}  ${w}x${h}\nhour ${light.hour.toFixed(1)}  day ${light.day.toFixed(2)}\nchapter ${chapter}  zoom ${cam.z.toFixed(2)}`;
  }

  requestAnimationFrame(frame);
}

// ---- place: the light follows the visitor's clock and location; use the real location if already allowed ----
navigator.permissions?.query({ name: 'geolocation' }).then((r) => {
  if (r.state !== 'granted' || !navigator.geolocation) return;
  navigator.geolocation.getCurrentPosition(
    (pos) => { setLocation(pos.coords.latitude, pos.coords.longitude); lightClock = 1; },
    () => {},
    { timeout: 8000, maximumAge: 600000 },
  );
}).catch(() => {});

showChapter(0);
placeHint();
// a link like #turning-point opens that project; #architecture goes to the district
const fromProject = PROJECTS.find((p) => `#${p.id}` === location.hash);
if (fromProject && projChapter(fromProject.id) > 0) {
  const pc = projChapter(fromProject.id);
  chapter = pc;
  camTarget = camFor(pc);
  Object.assign(cam, camTarget);
  showChapter(pc);
  openProject(fromProject.id);
}
// a link to #well and so on opens that place
const fromHash = chapters.findIndex((c, i) => i > 0 && `#${c.id}` === location.hash);
if (fromHash > 0) {
  chapter = fromHash;
  camTarget = camFor(fromHash);
  Object.assign(cam, camTarget);
  showChapter(fromHash);
  openChapter(fromHash);
}
requestAnimationFrame(frame);
requestAnimationFrame(() => requestAnimationFrame(() => { (window as unknown as { __pidReady?: boolean }).__pidReady = true; document.getElementById('boot')?.setAttribute('hidden', ''); intro.ready(); }));

// small hook so automated checks can drive the map (debug mode only)
if (debug) {
  (window as unknown as { __pid: unknown }).__pid = {
    go: (i: number, open?: boolean) => go(i, open),
    get chapter() { return chapter; },
    get quality() { return quality; },
    get points() { return grass.n; },
    get zoom() { return cam.z; },
    get cost() { return cost; },
  };
}
